import axios, { AxiosResponse } from 'axios';

/**
 * Normalized shape of a single Discovery Engine search result, mirroring
 * `SearchResponse.SearchResult` from the Google Discovery Engine (Agent
 * Search) API. Only fields that Google actually returns are modeled here;
 * everything else surfaces through `structData` / `derivedStructData` so the
 * UI can render whatever the PLA Contracts engine actually provides without
 * the frontend guessing at a schema.
 */
export interface AgentSearchResult {
  /** Result identifier, as returned by Discovery Engine. */
  id: string;
  document?: {
    /** Fully-qualified Discovery Engine document resource name. */
    name?: string;
    /** Document identifier. */
    id?: string;
    /** Structured data attached to the document at ingestion time. */
    structData?: Record<string, unknown>;
    /** Server-derived fields (e.g. title, link, snippets, extractive answers). */
    derivedStructData?: Record<string, unknown>;
  };
  /** Keyword vs. semantic scoring metadata Discovery Engine attaches to each result. */
  rankSignals?: Record<string, unknown>;
  /** Identifies whether this result came from keyword, semantic, or other retrieval. */
  retrievalSignals?: {
    retrievalSources?: string[];
    semanticRelevanceScore?: number;
    [key: string]: unknown;
  };
}

/** Normalized response returned by Google Agent Search (Discovery Engine). */
export interface AgentSearchResponse {
  results: AgentSearchResult[];
  totalSize?: number;
  nextPageToken?: string;
}

export interface AgentSearchRequest {
  query: string;
  pageSize?: number;
  pageToken?: string;
  queryExpansionSpec?: { condition: 'AUTO' };
  spellCorrectionSpec?: { mode: 'AUTO' };
  languageCode?: string;
  userInfo?: { timeZone?: string };
  /**
   * Discovery Engine metadata filter expression (e.g.
   * `data.summary.tenderCompany: ANY("Huawei")`), built via
   * `buildAgentSearchFilter` from `../filter`. Omitted entirely when there
   * is no active filter, which preserves Agent Search's default
   * (unfiltered) query behavior.
   */
  filter?: string;
}

/**
 * Client for the Google Agent Search (Discovery Engine) `pla-contracts`
 * engine. The full `:search` endpoint only supports OAuth (service-account)
 * authentication, not API keys, so the browser calls a Vercel API route
 * (`/api/agent-search/pla-contracts`) that proxies the request to Google
 * using server-side-only credentials. The service-account key, OAuth access
 * token, and Google credentials never reach the browser.
 */
export class SearchApi {
  private static PROXY_URL = '/api/agent-search/pla-contracts';

  static searchPlaContracts(
    query: string,
    pageSize = 25,
    pageToken: string | null = null,
    filter?: string,
  ): Promise<AxiosResponse<AgentSearchResponse>> {
    const body: AgentSearchRequest = {
      query,
      pageSize,
      pageToken: pageToken || undefined,
      queryExpansionSpec: { condition: 'AUTO' },
      spellCorrectionSpec: { mode: 'AUTO' },
      languageCode: 'en-US',
      userInfo: {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
      },
      filter: filter || undefined,
    };

    return axios.post<AgentSearchResponse>(this.PROXY_URL, body);
  }
}
